import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Home, SlidersHorizontal, X, Eye } from 'lucide-react';
import { shortViews } from './elements/listingFormat.jsx';
import LoopVideo, { filmSrc } from './elements/loopVideo.jsx';
import ReelViewer from './elements/reelViewer.jsx';
import { exploreDesigns, searchDesigns } from './elements/designsApi.jsx';
import { filterParams, activeCount } from './search.jsx';
import { ALL_PROVINCES } from './elements/provinces.jsx';
import './explore.scss';

// اکسپلور: طرح‌های مشاورها (فقط آنهایی که آگهی دارند) با فیلمِ کوتاهشان، مثلِ اکسپلورِ اینستاگرام — خانه‌های
// کوچک و هر چند خانه یک خانه‌ی بزرگ. فقط فیلم‌هایی که روی صفحه‌اند پخش می‌شوند. لمس = نمای تمام‌صفحه (ریلز)
// province: استانِ انتخاب‌شده در نوارِ بالا («همه‌ی ایران» = بدونِ فیلتر)
// kind: 'all' (همه‌ی طراحی‌ها — طراحی‌های کاربران هم) | 'real' (فقط آگهی‌های مشاورین املاک)
// applied: فیلترهای پنلِ «فیلتر» (همان پنلِ تبِ جست‌وجو) — اگر هست، فقط آگهی‌هایی که با آن جورند (آگهیِ واقعی)

const fa = (n) => Number(n).toLocaleString('fa-IR');
const isLarge = (i) => i % 10 === 2 || i % 10 === 7; // الگوی ثابت: دو خانه‌ی بزرگ در هر ۱۰ طرح

function badgeText(l) {
  if (!l) return '';
  if (l.dealType === 'rent' && (l.deposit || l.rent)) return l.deposit ? `ودیعه ${fa(Math.round(l.deposit / 1e6))}م` : `اجاره ${fa(Math.round(l.rent / 1e6))}م`;
  if (l.price) return l.price >= 1e9 ? `${fa(Math.round(l.price / 1e8) / 10)} میلیارد` : `${fa(Math.round(l.price / 1e6))} میلیون`;
  return l.area ? `${fa(l.area)} متر` : '';
}

function ExplorePage({ province, kind = 'all', applied = null, onOpenFilter, onClearFilter }) {
  const [items, setItems] = useState([]);
  const [next, setNext] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [open, setOpen] = useState(null); // ایندکسِ طرحِ بازشده در ریلز
  const loadingRef = useRef(false);
  const sentinelRef = useRef(null);
  const filter = province && province !== ALL_PROVINCES ? province : '';
  const fParams = filterParams(applied);
  const nFilters = activeCount(fParams);
  const fKey = nFilters ? JSON.stringify(fParams) : '';

  const load = useCallback(
    async (offset, replace) => {
      if (loadingRef.current) return;
      loadingRef.current = true;
      setLoading(true);
      setError('');
      try {
        const r = fKey
          ? await searchDesigns({ ...JSON.parse(fKey), province: filter, sort: 'new' }, offset, 24)
          : await exploreDesigns(filter, offset, 24, kind);
        setItems((prev) => {
          if (replace) return r.items || [];
          const have = new Set(prev.map((x) => x.id));
          return [...prev, ...(r.items || []).filter((x) => !have.has(x.id))];
        });
        setNext(r.next);
      } catch (e) {
        setError(e.message);
      } finally {
        loadingRef.current = false;
        setLoading(false);
      }
    },
    [filter, kind, fKey]
  );

  // استان عوض شد → از اول
  useEffect(() => {
    setItems([]);
    setNext(0);
    load(0, true);
  }, [load]);

  const loadMore = useCallback(() => {
    if (next != null && !loadingRef.current) load(next, false);
  }, [next, load]);

  // اسکرولِ بی‌پایان
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') return undefined;
    const io = new IntersectionObserver(([e]) => e.isIntersecting && loadMore(), { rootMargin: '600px 0px' });
    io.observe(el);
    return () => io.disconnect();
  }, [loadMore]);

  return (
    <div className="explore-page">
      {nFilters > 0 && (
        <div className="explore-page__filters">
          <button type="button" className="is-on" onClick={onOpenFilter}>
            <SlidersHorizontal size={14} strokeWidth={2} />
            <span>فیلترها ({fa(nFilters)})</span>
          </button>
          <button type="button" onClick={onClearFilter}>
            <X size={13} strokeWidth={2} />
            <span>حذف فیلترها</span>
          </button>
        </div>
      )}
      <section className="explore-page__grid">
        {items.map((d, i) => (
          <button
            key={d.id}
            type="button"
            className={`explore-page__grid-item ${isLarge(i) ? 'is-large' : ''}`}
            onClick={() => setOpen(i)}
          >
            {d.filmUrl ? (
              <LoopVideo src={filmSrc(d, true)} poster={d.thumbUrl} threshold={0.5} />
            ) : d.thumbUrl ? (
              <img src={d.thumbUrl} alt="" loading="lazy" />
            ) : (
              <Home size={22} strokeWidth={1.3} />
            )}
            {badgeText(d.listing) && <span className="explore-page__badge">{badgeText(d.listing)}</span>}
            <span className="explore-page__views">
              <Eye size={12} strokeWidth={2.2} />
              {shortViews(d.views)}
            </span>
          </button>
        ))}
        {loading &&
          Array.from({ length: items.length ? 3 : 9 }).map((_, i) => (
            <div key={`s${i}`} className={`explore-page__grid-item is-loading ${!items.length && isLarge(i) ? 'is-large' : ''}`} />
          ))}
      </section>

      {!loading && !error && items.length === 0 && (
        <div className="explore-page__empty">
          <Home size={30} strokeWidth={1.3} />
          <p>
            {nFilters
              ? 'آگهی‌ای با این فیلترها پیدا نشد.'
              : kind === 'real'
                ? filter
                  ? `هنوز فایلِ مشاورِ املاکی در ${filter} نیست.`
                  : 'هنوز فایلِ مشاورِ املاکی ثبت نشده.'
                : filter
                  ? `هنوز طراحی‌ای در ${filter} نیست.`
                  : 'هنوز طراحی‌ای ثبت نشده.'}
          </p>
        </div>
      )}
      {error && (
        <div className="explore-page__empty">
          <p>{error}</p>
          <button type="button" onClick={() => load(items.length ? next || 0 : 0, !items.length)}>
            تلاشِ دوباره
          </button>
        </div>
      )}
      <div ref={sentinelRef} style={{ height: 1 }} />

      {open !== null && items[open] && (
        <ReelViewer items={items} startIndex={open} onClose={() => setOpen(null)} onEndReached={loadMore} />
      )}
    </div>
  );
}

export default ExplorePage;
